import * as logging from 'utils/Logging'

import { borrowerApiClient } from 'api/BorrowerApiClient'
import { useState } from 'react'

/**
 * Cached because the header renders on every authenticated page and the name never changes within a session.
 * Cleared by the logout route, which wipes `sessionStorage`.
 */
const FIRST_NAME_KEY = 'fluro_customer_first_name'

interface NotificationProps {
  notifications: number
  firstName: string
  getNotificationsSummary: () => void
  getFirstName: () => void
}

const UseApiRequest = (): NotificationProps => {
  const borrowerApi = borrowerApiClient()
  const [notifications, setNotifications] = useState(0)
  const [firstName, setFirstName] = useState<string>(() => readCachedFirstName() ?? '')

  const getNotificationsSummary = () => {
    borrowerApi.getNotificationsSummary().then(
      (value) => {
        if (value.unread) {
          setNotifications(value.unread)
        }
      },
      (error) => {
        logging.logError('AccountMenu getNotificationsSummary', error)
      },
    )
  }

  /**
   * The customer's first name, from the account-details endpoint.
   *
   * It used to come free with the Okta id token's `name` claim. auth-service's access token carries
   * `sub` (email), `uid`, `groups` and `client_type` and no display name — nor does `/v1/me` — so the name
   * now has to be fetched. Skipped entirely when the cached value is already in hand.
   */
  const getFirstName = () => {
    if (readCachedFirstName()) return

    borrowerApi.getAccountDetails().then(
      (accountDetails) => {
        setFirstName(accountDetails.firstName)
        cacheFirstName(accountDetails.firstName)
      },
      (error) => {
        // Not worth interrupting the page for — the menu simply renders without a name.
        logging.logError('AccountMenu getAccountDetails', error)
      },
    )
  }

  return {
    notifications,
    firstName,
    getNotificationsSummary,
    getFirstName,
  }
}

function readCachedFirstName(): string | null {
  try {
    return window.sessionStorage.getItem(FIRST_NAME_KEY)
  } catch {
    return null
  }
}

function cacheFirstName(name: string): void {
  try {
    window.sessionStorage.setItem(FIRST_NAME_KEY, name)
  } catch {
    // Storage unavailable; the name is refetched on the next page load.
  }
}

export default UseApiRequest
